/**
 * ErrorDebugPanel Component
 * 
 * Painel de debug para visualizar erros capturados em produção.
 * Acesse via URL: adicione ?debug=true na URL para ativar
 */

import { useState, useEffect } from 'react';
import { X, AlertTriangle, Download, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

interface ErrorLog {
  timestamp: string;
  message: string;
  stack?: string;
  componentStack?: string;
  userAgent?: string;
  url?: string;
}

export function ErrorDebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);

  useEffect(() => {
    // Verificar se debug está ativo via URL
    const params = new URLSearchParams(window.location.search);
    const debugMode = params.get('debug') === 'true';
    
    if (debugMode) {
      setIsOpen(true);
      loadErrors();
    }

    // Adicionar atalho de teclado: Ctrl+Shift+D
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
        loadErrors();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const loadErrors = () => {
    try {
      // Combinar erros do ErrorBoundary e erros globais
      const appErrors = JSON.parse(localStorage.getItem('app_errors') || '[]');
      const globalErrors = JSON.parse(localStorage.getItem('global_errors') || '[]');
      
      // Unificar formato
      const allErrors = [
        ...appErrors,
        ...globalErrors.map((err: any) => ({
          ...err,
          componentStack: err.filename ? `${err.filename}:${err.lineno}:${err.colno}` : undefined,
        }))
      ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      setErrors(allErrors);
    } catch (e) {
      console.error('Failed to load errors:', e);
    }
  };

  const clearErrors = () => {
    localStorage.removeItem('app_errors');
    localStorage.removeItem('global_errors');
    setErrors([]);
    setSelectedError(null);
  };

  const downloadErrors = () => {
    const data = JSON.stringify(errors, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `errors-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-gray-900 text-white border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <h2 className="text-lg font-semibold">Painel de Debug - Erros</h2>
            <span className="px-2 py-1 text-xs bg-red-500 rounded">
              {errors.length} {errors.length === 1 ? 'erro' : 'erros'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={downloadErrors}
              disabled={errors.length === 0}
              className="text-white hover:bg-gray-800"
            >
              <Download className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearErrors}
              disabled={errors.length === 0}
              className="text-white hover:bg-gray-800"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-gray-800"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {errors.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <AlertTriangle className="w-12 h-12 mb-2" />
              <p>Nenhum erro registrado</p>
              <p className="text-sm mt-1">Os erros aparecerão aqui quando ocorrerem</p>
            </div>
          ) : (
            <div className="space-y-4">
              {errors.map((error, index) => (
                <div
                  key={index}
                  className="p-4 bg-gray-800 rounded-lg border border-gray-700 cursor-pointer hover:bg-gray-750 transition-colors"
                  onClick={() => setSelectedError(selectedError === error ? null : error)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-mono text-sm text-red-400 mb-1">
                        {error.message}
                      </p>
                      <p className="text-xs text-gray-400">
                        {new Date(error.timestamp).toLocaleString('pt-BR')}
                      </p>
                      {error.url && (
                        <p className="text-xs text-gray-500 mt-1">
                          URL: {error.url}
                        </p>
                      )}
                    </div>
                  </div>

                  {selectedError === error && (
                    <div className="mt-4 space-y-4">
                      {error.stack && (
                        <div>
                          <p className="text-xs text-gray-400 mb-1 font-semibold">Stack Trace:</p>
                          <pre className="text-xs bg-gray-900 p-3 rounded overflow-x-auto border border-gray-700">
                            {error.stack}
                          </pre>
                        </div>
                      )}

                      {error.componentStack && (
                        <div>
                          <p className="text-xs text-gray-400 mb-1 font-semibold">Component Stack:</p>
                          <pre className="text-xs bg-gray-900 p-3 rounded overflow-x-auto border border-gray-700">
                            {error.componentStack}
                          </pre>
                        </div>
                      )}

                      {error.userAgent && (
                        <div>
                          <p className="text-xs text-gray-400 mb-1 font-semibold">User Agent:</p>
                          <p className="text-xs text-gray-300 font-mono">
                            {error.userAgent}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 bg-gray-850">
          <p className="text-xs text-gray-400">
            💡 Dica: Pressione <kbd className="px-2 py-1 bg-gray-700 rounded">Ctrl+Shift+D</kbd> para abrir/fechar este painel
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Ou adicione <code className="px-1 bg-gray-700 rounded">?debug=true</code> na URL
          </p>
        </div>
      </Card>
    </div>
  );
}

